import {
    BaseCommentWithMentionsAndUser,
    CommentMention,
    CommentWithMentionsAndUser,
    DailyScrumCommentWithReplies,
    FeedbackCommentWithUser,
} from '@/common/types/databaseTypes';
import {
    useDeleteComment,
    useUpdateComment,
} from '@/features/comment/services/useCommentQueries';
import { Paper, Stack } from '@mantine/core';
import React, { useMemo, useState } from 'react';
import { CommentEditForm } from './CommentEditForm';
import { CommentForm } from './CommentForm';
import { CommentItem } from './CommentItem';
import { convertJsonToPlainTextWithMentions } from '../utils/mentionUtils';
import { useCommentDeleteModalStore } from '@/stores/commentDeleteModalStore';
import { adaptComment } from '../services/commentTypeService';
import {
    useDeleteFeedbackComment,
    useUpdateFeedbackComment,
} from '@/features/performance/feedback/services/useFeedbackQueries';

import { FeedbackCommentForm } from '@/features/performance/feedback/components/FeedbackCommentForm';
import { DailyScrumCommentForm } from '@/features/dailyscrum/components/DailyScrumCommentForm';
import {
    useDeleteDailyScrumComment,
    useUpdateDailyScrumComment,
} from '@/features/dailyscrum/services/useDailyScrumCommentQueries';

interface CommentListProps {
    comments: (
        | CommentWithMentionsAndUser
        | FeedbackCommentWithUser
        | DailyScrumCommentWithReplies
    )[];
    targetId: string;
    targetType: 'goal' | 'todo' | 'note' | 'feedback' | 'daily_scrum';
    isLoading?: boolean;
}

export const CommentList: React.FC<CommentListProps> = ({
    comments,
    targetId,
    targetType,
    isLoading,
}) => {
    // 모든 댓글을 공통 형식으로 변환
    const adaptedComments = useMemo(
        () => comments.map(comment => adaptComment(comment)),
        [comments]
    );

    // 상태 관리
    const [editingCommentId, setEditingCommentId] = useState<string | null>(
        null
    );
    const [editContent, setEditContent] = useState('');
    const [mentionedUsers, setMentionedUsers] = useState<CommentMention[]>([]);
    const [replyingToId, setReplyingToId] = useState<string | null>(null);
    const [replyContent, setReplyContent] = useState('');
    const [collapsedReplies, setCollapsedReplies] = useState<
        Record<string, boolean>
    >({});

    const { openModal: openCommentDeleteModal } = useCommentDeleteModalStore();

    // API 뮤테이션
    const updateFeedbackCommentMutation = useUpdateFeedbackComment();
    const updateDailyScrumCommentMutation = useUpdateDailyScrumComment();
    const updateBasicCommentMutation = useUpdateComment(
        targetType as 'goal' | 'todo' | 'note',
        targetId
    );

    const deleteFeedbackCommentMutation = useDeleteFeedbackComment();
    const deleteDailyScrumCommentMutation = useDeleteDailyScrumComment();
    const deleteBasicCommentMutation = useDeleteComment(
        targetType as 'goal' | 'todo' | 'note',
        targetId
    );

    const updateCommentMutation =
        targetType === 'feedback'
            ? updateFeedbackCommentMutation
            : targetType === 'daily_scrum'
              ? updateDailyScrumCommentMutation
              : updateBasicCommentMutation;

    const deleteCommentMutation =
        targetType === 'feedback'
            ? deleteFeedbackCommentMutation
            : targetType === 'daily_scrum'
              ? deleteDailyScrumCommentMutation
              : deleteBasicCommentMutation;

    // 상태 초기화 함수
    const resetEditState = () => {
        setEditingCommentId(null);
        setEditContent('');
        setMentionedUsers([]);
    };

    const resetReplyState = () => {
        setReplyingToId(null);
        setReplyContent('');
        setMentionedUsers([]);
    };

    // 댓글 수정 제출 핸들러
    const handleEditSubmit = (e: React.FormEvent, commentId: string) => {
        e.preventDefault();
        if (editContent.trim()) {
            if (targetType === 'daily_scrum') {
                updateDailyScrumCommentMutation.mutate({
                    commentId,
                    content: editContent.trim(),
                });
            } else if (targetType === 'feedback') {
                updateFeedbackCommentMutation.mutate({
                    feedbackCommentId: commentId,
                    content: editContent.trim(),
                    // mentions: mentionedUsers,
                });
            } else {
                updateBasicCommentMutation.mutate({
                    commentId,
                    content: editContent.trim(),
                    mentions: mentionedUsers,
                });
            }
            resetEditState();
        }
    };

    // 댓글 수정 버튼 클릭 핸들러
    const handleEditClick = (comment: BaseCommentWithMentionsAndUser) => {
        setEditingCommentId(comment.id);
        setEditContent(comment.content);
        setMentionedUsers(
            comment.mentions.map(mention => ({
                user_id: mention.user_id,
                user_name: mention.mention_user?.name,
            }))
        );
        setReplyingToId(null);
    };

    // 댓글 삭제 핸들러
    const handleDeleteClick = (comment: BaseCommentWithMentionsAndUser) => {
        openCommentDeleteModal({
            targetId: comment.id,
            targetType: 'comment',
            hasReply: comment.children?.length > 0,
            onConfirm: () => deleteCommentMutation.mutate(comment.id),
        });
    };

    // 답글 버튼 클릭 핸들러
    const handleReplyClick = (commentId: string) => {
        if (replyingToId === commentId) {
            resetReplyState();
            return;
        }

        setReplyingToId(commentId);
        setReplyContent('');
        setEditingCommentId(null);
        setMentionedUsers([]);
    };

    // 답글 토글 핸들러
    const toggleReplies = (commentId: string) => {
        setCollapsedReplies(prev => ({
            ...prev,
            [commentId]: !prev[commentId],
        }));
    };

    // 댓글 수정 폼 렌더링 함수
    const renderEditForm = (comment: BaseCommentWithMentionsAndUser) => {
        return (
            <CommentEditForm
                value={editContent}
                onChange={(content, mentions, json) => {
                    setMentionedUsers(
                        mentions.map(mention => ({
                            user_id: mention.id,
                            user_name: mention.label,
                        }))
                    );
                    const plainTextWithMentions =
                        convertJsonToPlainTextWithMentions(json);
                    setEditContent(plainTextWithMentions);
                }}
                onSubmit={e => handleEditSubmit(e, comment.id)}
                onCancel={resetEditState}
                isPending={updateCommentMutation.isPending}
                targetType={targetType}
                targetId={targetId}
            />
        );
    };

    // 답글 작성 폼 렌더링 함수
    const renderReplyForm = (comment: BaseCommentWithMentionsAndUser) => {
        if (comment.target_type === 'feedback') {
            return (
                <FeedbackCommentForm
                    targetId={comment.target_id}
                    parentId={comment.id}
                />
            );
        }

        if (targetType === 'daily_scrum') {
            return (
                <DailyScrumCommentForm
                    itemId={targetId}
                    parentId={comment.id}
                />
            );
        }

        return (
            <CommentForm
                parentId={comment.id}
                targetId={targetId}
                targetType={targetType}
            />
        );
    };

    // 최종 렌더링
    return (
        <Stack gap="sm" mb="sm">
            {adaptedComments.map(comment => {
                return (
                    <Paper
                        key={comment.id}
                        p="sm"
                        bg="transparent"
                        shadow="none"
                        radius="md"
                        withBorder
                    >
                        <CommentItem
                            comment={comment}
                            editingCommentId={editingCommentId}
                            replyingToId={replyingToId}
                            onReplyClick={handleReplyClick}
                            onEditClick={handleEditClick}
                            onDeleteClick={handleDeleteClick}
                            renderEditForm={renderEditForm}
                            renderReplyForm={renderReplyForm}
                            isCollapsed={!!collapsedReplies[comment.id]}
                            onToggleReplies={toggleReplies}
                        />
                    </Paper>
                );
            })}
        </Stack>
    );
};
